import React, { useMemo, useState } from 'react';
import { generatePrivateKey, privateKeyToAccount } from 'viem/accounts';
import { CheckCircle2, XCircle, FileWarning } from 'lucide-react';
import { PROMISES, claimIdFor } from '@/lib/agentReady';
import { ExitRecordPanel } from '@/components/agentready/ExitRecordPanel';

const short = (h) => `${h.slice(0, 10)}...${h.slice(-6)}`;

export function CheckResults({ results }) {
  const vendorKey = useMemo(() => privateKeyToAccount(generatePrivateKey()), []);
  const [claims, setClaims] = useState({});
  const label = Object.fromEntries(PROMISES);
  const file = async (r) => {
    const claimId = claimIdFor(r.id, r.evidence);
    if (claims[r.id]) return setClaims((c) => ({ ...c, [r.id]: { ...c[r.id], repeats: c[r.id].repeats + 1 } }));
    const receivedAt = new Date().toISOString().replace('T', ' ').slice(0, 19) + ' UTC';
    const sig = await vendorKey.signMessage({ message: `${claimId}|${receivedAt}` });
    setClaims((c) => ({ ...c, [r.id]: { claimId, receivedAt, sig, repeats: 0 } }));
  };
  return (
    <ul className="space-y-3" data-testid="ar-results">
      {results.map((r) => {
        const c = claims[r.id];
        return (
          <li key={r.id} className={`rounded-xl border p-4 ${r.ok ? 'border-emerald-500/30' : 'border-red-500/40'} bg-black/30`} data-testid={`ar-result-${r.id}`}>
            <div className="flex items-center gap-2 text-sm font-semibold text-white">
              {r.ok ? <CheckCircle2 className="w-4 h-4 text-emerald-400" /> : <XCircle className="w-4 h-4 text-red-400" />}
              {label[r.id]} <span className={`ml-auto text-xs ${r.ok ? 'text-emerald-300' : 'text-red-300'}`} data-testid={`ar-result-${r.id}-status`}>{r.ok ? 'Kept' : 'Broken'}</span>
            </div>
            <p className="text-xs text-gray-400 mt-1.5 break-words">{r.evidence}</p>
            {!r.ok && (
              <button type="button" onClick={() => file(r)} className="mt-3 mr-2 inline-flex items-center gap-1.5 rounded-full border border-amber-400/50 px-3 py-1 text-xs font-semibold text-amber-200 hover:bg-amber-500/10 transition-colors" data-testid={`ar-claim-btn-${r.id}`}>
                <FileWarning className="w-3.5 h-3.5" /> {c ? 'Send the claim again' : 'File an AX claim'}
              </button>
            )}
            {!r.ok && <ExitRecordPanel promiseId={r.id} vendorKey={vendorKey} failedEvidence={r.evidence} />}
            {c && (
              <div className="mt-2 rounded-lg border border-white/10 p-2.5 font-mono text-[11px] text-gray-400 break-all" data-testid={`ar-claim-receipt-${r.id}`}>
                <div>claim {short(c.claimId)} received {c.receivedAt}</div>
                <div>signed receipt {short(c.sig)}</div>
                <div className="text-gray-500 font-sans mt-1">Status: received. {c.repeats > 0 ? `Sent ${c.repeats + 1} times, one claim, same receipt.` : 'The vendor decides under its own terms. Kojiru makes no decision.'}</div>
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}
